import { AuthResponse } from '@/api/auth/interfaces';
import Cookies from 'universal-cookie';
import { setAccessTokenCookie, removeAccessTokenCookie, AUTH_COOKIE_OPTIONS } from '@/utils/authCookies';
import { apiPost } from '@/api/_shared/http';
import { AUTH_BASE } from '@/api/auth/paths';

// Utility function to handle access token storage
const cookies = new Cookies();

export const handleAccessTokenStorage = (data: AuthResponse) => {
  if (data.accessToken) {
setAccessTokenCookie(data.accessToken);
    console.log('[LoginFlow] handleAccessTokenStorage: cookie set, dispatching auth:changed');
    window.dispatchEvent(new Event('auth:changed'));
  }
};

export const clearAccessTokenStorage = () => {
  removeAccessTokenCookie();
  clearCasSessionMarker();
  // Notify app that auth state has changed
  window.dispatchEvent(new Event('auth:changed'));
};

const AUTH_PROVIDER_COOKIE = 'authProvider';
const CAS_AUTH_PROVIDER = 'cas';

/**
 * Remembers that the current session was started through CAS, so logout also ends the CAS SSO session.
 */
export const markCasSession = () => {
  cookies.set(AUTH_PROVIDER_COOKIE, CAS_AUTH_PROVIDER, AUTH_COOKIE_OPTIONS);
};

/**
 * Whether the current session was started through CAS.
 */
export const isCasSession = (): boolean => cookies.get(AUTH_PROVIDER_COOKIE) === CAS_AUTH_PROVIDER;

/**
 * Forgets the CAS session marker (called on logout together with the access token).
 */
export const clearCasSessionMarker = () => {
  cookies.remove(AUTH_PROVIDER_COOKIE, { path: '/' });
};

const POPUP_AUTH_TIMEOUT_MS = 5 * 60 * 1000;
const POPUP_CANCEL_FOCUS_DELAY_MS = 400;

function safeClosePopup(popup: Window | null): void {
  if (!popup) return;
  try {
    popup.close();
  } catch {
    // COOP blocks close() while the popup is on a cross-origin page (e.g. Google).
  }
}

// Utility function to handle popup-based OAuth flows
export const handlePopupFlow = (
  url: string,
  windowName: string,
  successMessageType: string,
  errorMessageType: string,
  onSuccess?: (data: any) => void
): Promise<any> => {
  return new Promise((resolve, reject) => {
    const popup = window.open(url, windowName, 'width=500,height=600,scrollbars=yes,resizable=yes');

    if (!popup) {
      reject({ error: 'Popup was blocked. Please allow popups for this site.' });
      return;
    }

    let settled = false;
    let cancelCheckTimer: ReturnType<typeof setTimeout> | undefined;
    let authTimeout: ReturnType<typeof setTimeout> | undefined;

    const cleanup = () => {
      window.removeEventListener('message', messageListener);
      window.removeEventListener('focus', onWindowFocus);
      if (cancelCheckTimer) clearTimeout(cancelCheckTimer);
      if (authTimeout) clearTimeout(authTimeout);
      safeClosePopup(popup);
    };

    const settle = (handler: () => void) => {
      if (settled) return;
      settled = true;
      cleanup();
      handler();
    };

    const messageListener = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) {
        return;
      }

      if (event.data.type === successMessageType) {
        if (onSuccess) {
          onSuccess(event.data.payload);
        }
        if (event.data?.payload?.accessToken) {
setAccessTokenCookie(event.data.payload.accessToken);
          window.dispatchEvent(new Event('auth:changed'));
        }
        settle(() => resolve({ data: event.data.payload }));
      } else if (event.data.type === errorMessageType) {
        settle(() => reject({ error: event.data.error }));
      }
    };

    // Do not poll popup.closed — once the popup navigates to Google, COOP blocks that access.
    // When the user dismisses the popup, focus returns to this window.
    const onWindowFocus = () => {
      if (settled) return;
      if (cancelCheckTimer) clearTimeout(cancelCheckTimer);
      cancelCheckTimer = setTimeout(() => {
        if (settled) return;
        settle(() => reject({ error: 'Popup was closed' }));
      }, POPUP_CANCEL_FOCUS_DELAY_MS);
    };

    window.addEventListener('message', messageListener);
    window.addEventListener('focus', onWindowFocus);

    authTimeout = setTimeout(() => {
      settle(() => reject({ error: 'Authentication timed out' }));
    }, POPUP_AUTH_TIMEOUT_MS);
  });
};

// Utility function to handle connect flow with authorization URL fetch
export const handleConnectFlow = async (): Promise<any> => {
  return new Promise(async (resolve, reject) => {
    try {
      // First, get the authorization URL from the backend
      const { authorizeUrl } = await apiPost<{ authorizeUrl: string }>(`${AUTH_BASE}/google/connect`);

      // Handle the popup flow
      const result = await handlePopupFlow(authorizeUrl, 'googleConnect', 'GOOGLE_CONNECT_SUCCESS', 'GOOGLE_CONNECT_ERROR');

      resolve(result);
    } catch (error: any) {
      reject({ error: error?.message || 'Failed to connect Google account' });
    }
  });
};
